import React, { useState, useEffect, useRef, useMemo } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import {
  subscribeToConversations,
  subscribeToMessages,
  sendMessage,
  markAsRead,
  requestUnmask,
  cancelUnmask,
  deleteConversation,
  isDailyVibeShareInviteMessage,
  formatDailyVibeShareInviteDisplay,
  isDailyVibeShareResponseMessage,
  formatDailyVibeShareResponseDisplay,
  isDailyVibeCheckGifMessage,
  DAILY_VIBE_SHARE_ACCEPTED_TEXT,
  DAILY_VIBE_SHARE_DECLINED_TEXT,
  DAILY_VIBE_CHECK_GIF_TEXT,
} from '../../services/messagingService';
import {
  subscribeToDailyVibeShare,
  acceptDailyVibeShare,
  declineDailyVibeShare,
} from '../../utils/dailyVibeFirestore';
import { searchSpiritGifs } from '../../services/firebaseApiService';
import { getShuffledMoodAndHelloGifsAll } from '../../data/localGifLibraries';
import { DEFAULT_FOX_CHAT_COPY, FOX_DAILY_CHECK_INBOX_ID } from '../home/foxChatConfig';
import FoxChat from '../home/FoxChat';
import theFoxMini from '../../TheFoxMini.png';
import { db } from '../../firebase';
import {
  DAILY_MATCH_LIMIT,
  getDailyVibePeriodKey,
  hasDailyVibeCheckForPeriod,
  loadDailyVibeCheckForPeriod,
  readDailyVibeMatchIds,
  syncDailyVibeMatchIds,
} from '../../utils/dailyVibeFirestore';
import '../../styles/Messaging.css';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return date.toLocaleDateString(undefined, { weekday: 'short' });
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

function formatConversationPreview(lastMessage, currentUserId, otherDisplayName) {
  if (!lastMessage) return 'No messages yet';
  if (isDailyVibeShareInviteMessage(lastMessage)) {
    return formatDailyVibeShareInviteDisplay({
      isSender: lastMessage.senderId === currentUserId,
      senderName: otherDisplayName,
    });
  }
  const prefix = lastMessage.senderId === currentUserId ? 'You: ' : '';
  if (isDailyVibeCheckGifMessage(lastMessage)) {
    return `${prefix}${DAILY_VIBE_CHECK_GIF_TEXT}`;
  }
  return `${prefix}${lastMessage.text || 'No messages yet'}`;
}

function gifDedupeKey(g) {
  const u = (g && (g.fullUrl || g.gifUrl)) || '';
  return String(u).trim();
}

/** Local bundled GIFs first (fixed order for this picker open), then KLIPY; strip duplicates by URL. */
function mergeLocalThenKlipy(localGifs, remoteGifs) {
  const seen = new Set();
  const out = [];
  const push = (item) => {
    const k = gifDedupeKey(item);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(item);
  };
  (localGifs || []).forEach(push);
  (remoteGifs || []).forEach(push);
  return out;
}

/** Search: KLIPY hits first so top tiles match the query; locals follow, deduped. */
function mergeKlipyThenLocal(remoteGifs, localGifs) {
  const seen = new Set();
  const out = [];
  const push = (item) => {
    const k = gifDedupeKey(item);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(item);
  };
  (remoteGifs || []).forEach(push);
  (localGifs || []).forEach(push);
  return out;
}

const KLIPY_PICKER_PAGE = 40;

const Inbox = ({
  currentUser,
  onClose,
  initialConversationId = null,
  onOpenPartnerCollage,
  hasPrimaryCollage,
  onStartCollage,
  foxDailyCheckUnread = false,
  onDailyVibeCheckCompleted = null,
  onVerifyProfile = null,
  profileVerified = false,
  vibeCheckStreak = 0,
  vibeCheckStreakLastPeriodKey = null,
}) => {
  const [conversations, setConversations] = useState([]);
  const [foxDailyCheckResponded, setFoxDailyCheckResponded] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(initialConversationId);
  /* `__fox_daily_check__` is a UI-only sentinel for the embedded FoxChat thread, not a real
     Firestore doc id (Firestore rejects ids wrapped in double underscores). Treat it as
     "no real conversation" in any effect that queries `conversations/{id}`. */
  const isFoxDailyCheckThread = selectedConversationId === FOX_DAILY_CHECK_INBOX_ID;
  /** One-shot fetch when opening via deep link: snapshot list may not include the thread yet. */
  const [threadConvFallback, setThreadConvFallback] = useState(null);
  const [threadConvHydrating, setThreadConvHydrating] = useState(false);
  const [openingPartnerCollage, setOpeningPartnerCollage] = useState(false);
  const [menuOpenConvId, setMenuOpenConvId] = useState(null);
  const [deleteConfirmConv, setDeleteConfirmConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [unmasking, setUnmasking] = useState(false);
  const [unmaskNeedCollageOpen, setUnmaskNeedCollageOpen] = useState(false);
  /** Daily-vibe-share status for the open thread: null | 'pending' | 'accepted' | 'declined'. */
  const [dailyVibeShareStatus, setDailyVibeShareStatus] = useState(null);
  const [vibeShareActionPending, setVibeShareActionPending] = useState(false);
  /* "GET A NEW MATCH" request for the embedded FoxChat. Consumed (reset to 0) as soon as
     the user leaves that thread, so any later mount of it runs a plain daily check
     rather than burning another match. The button sets it together with the thread
     selection in one batch, so the reset below never races the click. */
  const [foxAnotherMatchToken, setFoxAnotherMatchToken] = useState(0);
  useEffect(() => {
    if (!isFoxDailyCheckThread) setFoxAnotherMatchToken(0);
  }, [isFoxDailyCheckThread]);
  const [matchesToday, setMatchesToday] = useState(0);
  useEffect(() => {
    if (!currentUser?.uid || selectedConversationId) return undefined;
    const periodKey = getDailyVibePeriodKey();
    setMatchesToday(readDailyVibeMatchIds(currentUser.uid, periodKey).length);
    let cancelled = false;
    (async () => {
      try {
        const check = await loadDailyVibeCheckForPeriod(db, currentUser.uid, periodKey);
        if (cancelled || !check) return;
        const ids = syncDailyVibeMatchIds(currentUser.uid, [
          ...(Array.isArray(check.matchConversationIds) ? check.matchConversationIds : []),
          check.matchConversationId,
        ], periodKey);
        if (!cancelled) setMatchesToday(ids.length);
      } catch (err) {
        if (!cancelled) console.warn('Daily match count load failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, selectedConversationId]);
  const [showChatGifPicker, setShowChatGifPicker] = useState(false);
  const [chatGifs, setChatGifs] = useState([]);
  const [chatGifSearch, setChatGifSearch] = useState('');
  const [loadingChatGifs, setLoadingChatGifs] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const localChatGifsBaseRef = useRef([]);
  const chatKlipyFetchGenRef = useRef(0);
  const chatGifGridScrollRef = useRef(null);
  const chatSpiritGifWarmupRef = useRef(false);
  /** Reset when leaving thread list → chat; warm KLIPY once per opened conversation (like reach-out). */
  const conversationSpiritGifWarmupRef = useRef(false);

  /** Same image source as navbar: primary collage userPhotoUrl, via userPublicPrimary.ad (readable for chat peers). */
  const [partnerPrimaryPhotoByUid, setPartnerPrimaryPhotoByUid] = useState({});
  const [convAvatarFailedByUid, setConvAvatarFailedByUid] = useState({});

  const partnerPreviewUidKey = useMemo(() => {
    if (!currentUser?.uid) return '';
    const ids = new Set();
    for (const c of conversations) {
      const o = c.participants?.find((id) => id !== currentUser.uid);
      if (o) ids.add(o);
    }
    if (selectedConversationId) {
      const conv =
        conversations.find((c) => c.id === selectedConversationId) || threadConvFallback;
      const o = conv?.participants?.find((id) => id !== currentUser.uid);
      if (o) ids.add(o);
    }
    return [...ids].sort().join(',');
  }, [conversations, currentUser?.uid, selectedConversationId, threadConvFallback]);

  useEffect(() => {
    setConvAvatarFailedByUid({});
  }, [partnerPreviewUidKey]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setFoxDailyCheckResponded(false);
      return undefined;
    }

    let cancelled = false;
    const applyResponded = (responded) => {
      if (!cancelled) setFoxDailyCheckResponded(responded);
    };

    applyResponded(hasDailyVibeCheckForPeriod(currentUser.uid));

    (async () => {
      try {
        const check = await loadDailyVibeCheckForPeriod(db, currentUser.uid);
        if (!cancelled) applyResponded(Boolean(check?.gifUrl));
      } catch {
        /* keep cache-based state */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setPartnerPrimaryPhotoByUid({});
      return;
    }
    const otherIds = partnerPreviewUidKey ? partnerPreviewUidKey.split(',').filter(Boolean) : [];
    if (otherIds.length === 0) {
      setPartnerPrimaryPhotoByUid({});
      return;
    }
    const unsubs = otherIds.map((uid) =>
      onSnapshot(
        doc(db, 'userPublicPrimary', uid),
        (snap) => {
          const raw = snap.exists() ? snap.data()?.ad : null;
          const url =
            raw && typeof raw.userPhotoUrl === 'string' && raw.userPhotoUrl.trim()
              ? raw.userPhotoUrl.trim()
              : null;
          setPartnerPrimaryPhotoByUid((prev) => {
            if (prev[uid] === url) return prev;
            return { ...prev, [uid]: url };
          });
        },
        () => {
          setPartnerPrimaryPhotoByUid((prev) => {
            if (!(uid in prev)) return prev;
            const next = { ...prev };
            delete next[uid];
            return next;
          });
        },
      ),
    );
    return () => {
      unsubs.forEach((u) => u());
    };
  }, [partnerPreviewUidKey, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeToConversations(currentUser.uid, setConversations);
  }, [currentUser?.uid]);

  /* Live daily-vibe-share status for the open 1:1 thread (drives ACCEPT/DECLINE buttons). */
  useEffect(() => {
    if (!currentUser?.uid || !selectedConversationId) {
      setDailyVibeShareStatus(null);
      return undefined;
    }
    const conv =
      conversations.find((c) => c.id === selectedConversationId) || threadConvFallback;
    const otherId = conv?.participants?.find((id) => id !== currentUser.uid);
    if (!otherId) {
      setDailyVibeShareStatus(null);
      return undefined;
    }
    return subscribeToDailyVibeShare(db, currentUser.uid, otherId, (share) => {
      setDailyVibeShareStatus(share?.status || null);
    });
  }, [currentUser?.uid, selectedConversationId, conversations, threadConvFallback]);

  useEffect(() => {
    if (initialConversationId) {
      setSelectedConversationId(initialConversationId);
    }
  }, [initialConversationId]);

  useEffect(() => {
    if (!selectedConversationId || isFoxDailyCheckThread || !currentUser?.uid) {
      setThreadConvFallback(null);
      setThreadConvHydrating(false);
      return;
    }
    const inList = conversations.some((c) => c.id === selectedConversationId);
    if (inList) {
      setThreadConvFallback(null);
      setThreadConvHydrating(false);
      return;
    }
    let cancelled = false;
    setThreadConvHydrating(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'conversations', selectedConversationId));
        if (cancelled) return;
        if (!snap.exists()) {
          setThreadConvFallback(null);
          return;
        }
        const data = snap.data();
        const parts = Array.isArray(data.participants) ? data.participants : [];
        if (!parts.includes(currentUser.uid)) {
          setThreadConvFallback(null);
          return;
        }
        setThreadConvFallback({ id: snap.id, ...data });
      } catch (err) {
        console.error('Failed to load conversation for deep link:', err);
        if (!cancelled) setThreadConvFallback(null);
      } finally {
        if (!cancelled) setThreadConvHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedConversationId, currentUser?.uid, conversations]);

  useEffect(() => {
    if (!selectedConversationId || isFoxDailyCheckThread) {
      setMessages([]);
      return undefined;
    }
    const unsub = subscribeToMessages(selectedConversationId, setMessages);
    if (currentUser?.uid) {
      markAsRead(selectedConversationId, currentUser.uid).catch(() => {});
    }
    return unsub;
  }, [selectedConversationId, isFoxDailyCheckThread, currentUser?.uid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (selectedConversationId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedConversationId]);

  // Mark messages as read while viewing
  useEffect(() => {
    if (!selectedConversationId || isFoxDailyCheckThread || !currentUser?.uid) return;
    const conv = conversations.find(c => c.id === selectedConversationId);
    if (conv?.unreadCount?.[currentUser.uid] > 0) {
      markAsRead(selectedConversationId, currentUser.uid).catch(() => {});
    }
  }, [messages, selectedConversationId, isFoxDailyCheckThread, currentUser?.uid, conversations]);

  useEffect(() => {
    if (!selectedConversationId) {
      conversationSpiritGifWarmupRef.current = false;
      return;
    }
    if (conversationSpiritGifWarmupRef.current) return;
    conversationSpiritGifWarmupRef.current = true;
    const customerId = currentUser?.uid || 'anonymous';
    searchSpiritGifs('warmup', customerId).catch(() => {});
  }, [selectedConversationId, currentUser?.uid]);

  useEffect(() => {
    if (!showChatGifPicker) {
      chatSpiritGifWarmupRef.current = false;
      return;
    }
    if (chatSpiritGifWarmupRef.current) return;
    chatSpiritGifWarmupRef.current = true;
    const customerId = currentUser?.uid || 'anonymous';
    searchSpiritGifs('warmup', customerId).catch(() => {});
  }, [showChatGifPicker, currentUser?.uid]);

  const appendKlipyGifs = async (query, isSearch = false) => {
    const gen = ++chatKlipyFetchGenRef.current;
    setLoadingChatGifs(true);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      const remote = await searchSpiritGifs(query, customerId, KLIPY_PICKER_PAGE);
      if (gen !== chatKlipyFetchGenRef.current) return;
      const merged = isSearch
        ? mergeKlipyThenLocal(remote, localChatGifsBaseRef.current)
        : mergeLocalThenKlipy(localChatGifsBaseRef.current, remote);
      setChatGifs(merged);
      if (isSearch) {
        requestAnimationFrame(() => {
          const el = chatGifGridScrollRef.current;
          if (el) el.scrollTop = 0;
        });
      }
    } catch (err) {
      console.error('Error fetching chat GIFs:', err);
    } finally {
      if (gen === chatKlipyFetchGenRef.current) {
        setLoadingChatGifs(false);
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending || !selectedConversationId) return;
    setSending(true);
    try {
      await sendMessage(selectedConversationId, currentUser.uid, text);
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (conversation) => {
    if (!conversation?.participantDetails || !currentUser?.uid) return { displayName: 'User', email: '' };
    const otherId = conversation.participants?.find(id => id !== currentUser.uid);
    return conversation.participantDetails[otherId] || { displayName: 'User', email: '' };
  };

  if (!selectedConversationId) {
    return (
      <div className="messaging-overlay" onClick={onClose}>
        <div className="messaging-panel" onClick={e => e.stopPropagation()}>
          <div className="messaging-header messaging-header--list">
            <span className="messaging-header-balance" aria-hidden="true" />
            <h3 className="messaging-title messaging-title--list">MESSAGES</h3>
            <button className="messaging-close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
          <div className="messaging-conversations">
            {currentUser?.uid && (
              <div
                className={`messaging-conversation-item messaging-conversation-item--fox${foxDailyCheckUnread ? ' unread' : ''}`}
                onClick={(e) => {
                  if (typeof onStartCollage === 'function') onStartCollage(e);
                  else setSelectedConversationId(FOX_DAILY_CHECK_INBOX_ID);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (typeof onStartCollage === 'function') onStartCollage(e);
                    else setSelectedConversationId(FOX_DAILY_CHECK_INBOX_ID);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <img
                  src={theFoxMini}
                  alt=""
                  className="messaging-conv-avatar messaging-conv-avatar-img fox-inbox-conv-avatar"
                />
                <div className="messaging-conv-info">
                  <div className="messaging-conv-name-row">
                    <span className="messaging-conv-name">
                      {DEFAULT_FOX_CHAT_COPY.loggedInChatHeaderTitle}
                    </span>
                    {foxDailyCheckUnread && (
                      <span className="messaging-conv-time">now</span>
                    )}
                  </div>
                  <div className="messaging-conv-preview-row">
                    <span className="messaging-conv-preview">
                      {foxDailyCheckResponded
                        ? DEFAULT_FOX_CHAT_COPY.dailyCheckComputingPreviewText
                        : DEFAULT_FOX_CHAT_COPY.dailyCheckGreetingText}
                    </span>
                    {foxDailyCheckUnread && <span className="messaging-conv-badge">1</span>}
                  </div>
                </div>
              </div>
            )}
            {conversations.length === 0 ? (
              <div className="messaging-empty-stack">
                <p className="messaging-empty">
                  Your vibe matches will show here.
                </p>
                {hasPrimaryCollage === false && (
                  <>
                    {typeof onStartCollage === 'function' && (
                      <button
                        type="button"
                        className="poster-collage-btn messaging-empty-poster-collage-btn messaging-make-collage-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStartCollage(e);
                        }}
                      >
                        Create your profile
                      </button>
                    )}
                    <p className="messaging-empty messaging-empty--followup">
                      complete your profile and meet a friend or a date
                    </p>
                  </>
                )}
              </div>
            ) : (
              conversations.map(conv => {
                const other = getOtherParticipant(conv);
                const unread = conv.unreadCount?.[currentUser.uid] || 0;
                const otherUidConv = conv.participants?.find(id => id !== currentUser.uid);
                const navbarMatchPhoto =
                  otherUidConv && partnerPrimaryPhotoByUid[otherUidConv]
                    ? partnerPrimaryPhotoByUid[otherUidConv]
                    : null;
                const showConvAvatarImg =
                  navbarMatchPhoto && !convAvatarFailedByUid[otherUidConv];
                return (
                  <div
                    key={conv.id}
                    className={`messaging-conversation-item ${unread > 0 ? 'unread' : ''}`}
                    onClick={() => setSelectedConversationId(conv.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <button
                      type="button"
                      className="messaging-conv-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenConvId(menuOpenConvId === conv.id ? null : conv.id);
                      }}
                      aria-label="More options"
                    >
                      ⋮
                    </button>
                    {menuOpenConvId === conv.id && (
                      <div className="messaging-conv-menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="messaging-conv-menu-delete"
                          onClick={() => {
                            setMenuOpenConvId(null);
                            setDeleteConfirmConv({ id: conv.id, displayName: other.displayName });
                          }}
                        >
                          DELETE CONVERSATION
                        </button>
                      </div>
                    )}
                    {showConvAvatarImg ? (
                      <img
                        src={navbarMatchPhoto}
                        alt=""
                        className="messaging-conv-avatar messaging-conv-avatar-img"
                        onError={() =>
                          setConvAvatarFailedByUid((prev) => ({ ...prev, [otherUidConv]: true }))
                        }
                      />
                    ) : (
                      <div className="messaging-conv-avatar" aria-hidden="true">
                        {(other.displayName || 'U')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="messaging-conv-info">
                      <div className="messaging-conv-name-row">
                        <span className="messaging-conv-name">{other.displayName}</span>
                        <span className="messaging-conv-time">{formatTime(conv.lastMessage?.timestamp)}</span>
                      </div>
                      <div className="messaging-conv-preview-row">
                        <span className="messaging-conv-preview">
                          {formatConversationPreview(conv.lastMessage, currentUser.uid, other.displayName)}
                        </span>
                        {unread > 0 && <span className="messaging-conv-badge">{unread}</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="messaging-see-friend-footer">
            <button
              type="button"
              className="messaging-see-friend-btn"
              disabled={matchesToday >= DAILY_MATCH_LIMIT}
              onClick={() => {
                if (matchesToday >= DAILY_MATCH_LIMIT) return;
                if (hasPrimaryCollage === false && typeof onStartCollage === 'function') {
                  onStartCollage();
                  return;
                }
                setFoxAnotherMatchToken((n) => n + 1);
                setSelectedConversationId(FOX_DAILY_CHECK_INBOX_ID);
              }}
            >
              {`GET A NEW MATCH (${Math.min(matchesToday, DAILY_MATCH_LIMIT)}/${DAILY_MATCH_LIMIT})`}
            </button>
            <p className="messaging-see-friend-hint">
              {`get up to ${DAILY_MATCH_LIMIT} new matches per day`}
            </p>
          </div>
          {deleteConfirmConv && (
            <div className="messaging-delete-overlay" onClick={() => setDeleteConfirmConv(null)}>
              <div className="messaging-delete-popup" onClick={(e) => e.stopPropagation()}>
                <p className="messaging-delete-text">
                  Are you sure you want to delete your chat with {deleteConfirmConv.displayName}?
                  <span className="messaging-delete-hint">This will delete your chat history for both you and {deleteConfirmConv.displayName}.</span>
                </p>
                <div className="messaging-delete-buttons">
                  <button
                    type="button"
                    className="messaging-delete-btn messaging-delete-btn-confirm"
                    onClick={async () => {
                      const convId = deleteConfirmConv.id;
                      setDeleteConfirmConv(null);
                      try {
                        await deleteConversation(convId);
                      } catch (err) {
                        console.error('Failed to delete conversation:', err);
                      }
                    }}
                  >
                    CONFIRM
                  </button>
                  <button
                    type="button"
                    className="messaging-delete-btn messaging-delete-btn-cancel"
                    onClick={() => setDeleteConfirmConv(null)}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (selectedConversationId === FOX_DAILY_CHECK_INBOX_ID) {
    return (
      <div className="messaging-overlay" onClick={onClose}>
        <FoxChat
          embeddedInInbox
          isOpen
          launcherVisible={false}
          currentUser={currentUser}
          onClose={onClose}
          onInboxBack={() => setSelectedConversationId(null)}
          onDailyVibeCheckCompleted={(payload) => {
            setFoxDailyCheckResponded(true);
            onDailyVibeCheckCompleted?.(payload);
          }}
          onOpenMessages={(conversationId) => setSelectedConversationId(conversationId || null)}
          onVerifyProfile={onVerifyProfile}
          profileVerified={profileVerified}
          vibeCheckStreak={vibeCheckStreak}
          vibeCheckStreakLastPeriodKey={vibeCheckStreakLastPeriodKey}
          requestAnotherMatchToken={foxAnotherMatchToken}
          hasSavedCollage={hasPrimaryCollage !== false}
          foxChatCopy={DEFAULT_FOX_CHAT_COPY}
        />
      </div>
    );
  }

  const selectedConv =
    conversations.find((c) => c.id === selectedConversationId) || threadConvFallback;

  if (threadConvHydrating && !selectedConv) {
    return (
      <div className="messaging-overlay" onClick={onClose}>
        <div className="messaging-panel messaging-panel-thread" onClick={(e) => e.stopPropagation()}>
          <div className="messaging-header">
            <button
              type="button"
              className="messaging-back"
              onClick={() => setSelectedConversationId(null)}
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="messaging-title">Opening chat…</h3>
            <button type="button" className="messaging-close" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
          <p className="messaging-empty" style={{ padding: '1.5rem' }}>Loading conversation…</p>
        </div>
      </div>
    );
  }

  if (!selectedConv) {
    return (
      <div className="messaging-overlay" onClick={onClose}>
        <div className="messaging-panel messaging-panel-thread" onClick={(e) => e.stopPropagation()}>
          <div className="messaging-header">
            <button
              type="button"
              className="messaging-back"
              onClick={() => setSelectedConversationId(null)}
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="messaging-title">Messages</h3>
            <button type="button" className="messaging-close" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
          <p className="messaging-empty" style={{ padding: '1.5rem' }}>
            This conversation isn&rsquo;t available. Sign in with the account that received the email, or open Messages from the home page.
          </p>
        </div>
      </div>
    );
  }

  const other = getOtherParticipant(selectedConv);

  const unmaskData = selectedConv?.unmask || {};
  const myUid = currentUser.uid;
  const otherUid = selectedConv?.participants?.find(id => id !== myUid);
  const iUnmasked = !!unmaskData[myUid]?.photoUrl;
  const theyUnmasked = otherUid ? !!unmaskData[otherUid]?.photoUrl : false;
  const bothUnmasked = iUnmasked && theyUnmasked;

  const threadPartnerPhoto = otherUid ? partnerPrimaryPhotoByUid[otherUid] : null;
  const showThreadHeaderAvatarImg =
    threadPartnerPhoto && !convAvatarFailedByUid[otherUid];

  const handleUnmask = async () => {
    if (unmasking || iUnmasked) return;
    if (hasPrimaryCollage === false) {
      setUnmaskNeedCollageOpen(true);
      return;
    }
    setUnmasking(true);
    try {
      await requestUnmask(selectedConversationId, myUid);
    } catch (err) {
      console.error('Unmask failed:', err);
      alert(err.message || 'Could not unmask. Please try again.');
    } finally {
      setUnmasking(false);
    }
  };

  const handleAcceptVibeShare = async () => {
    if (vibeShareActionPending || !otherUid) return;
    setVibeShareActionPending(true);
    try {
      await acceptDailyVibeShare(db, myUid, otherUid);
      await sendMessage(selectedConversationId, myUid, DAILY_VIBE_SHARE_ACCEPTED_TEXT, {
        isSystemMessage: true,
        dailyVibeShareAccepted: true,
      });
    } catch (err) {
      console.error('Accept vibe share failed:', err);
      alert('Could not accept. Please try again.');
    } finally {
      setVibeShareActionPending(false);
    }
  };

  const handleDeclineVibeShare = async () => {
    if (vibeShareActionPending || !otherUid) return;
    setVibeShareActionPending(true);
    try {
      await declineDailyVibeShare(db, myUid, otherUid);
      await sendMessage(selectedConversationId, myUid, DAILY_VIBE_SHARE_DECLINED_TEXT, {
        isSystemMessage: true,
        dailyVibeShareDeclined: true,
      });
    } catch (err) {
      console.error('Decline vibe share failed:', err);
      alert('Could not decline. Please try again.');
    } finally {
      setVibeShareActionPending(false);
    }
  };

  return (
    <div className="messaging-overlay" onClick={onClose}>
      <div className="messaging-panel messaging-panel-thread" onClick={e => e.stopPropagation()}>
        <div className="messaging-header">
          <button className="messaging-back" onClick={() => setSelectedConversationId(null)} aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="messaging-thread-title-block">
            {showThreadHeaderAvatarImg ? (
              <img
                src={threadPartnerPhoto}
                alt=""
                className="messaging-conv-avatar messaging-conv-avatar-img"
                onError={() =>
                  otherUid &&
                  setConvAvatarFailedByUid((prev) => ({ ...prev, [otherUid]: true }))
                }
              />
            ) : (
              <div className="messaging-conv-avatar" aria-hidden="true">
                {(other.displayName || 'U')[0].toUpperCase()}
              </div>
            )}
            {otherUid && typeof onOpenPartnerCollage === 'function' ? (
              <button
                type="button"
                className="messaging-title messaging-title--partner-collage"
                onClick={async () => {
                  if (openingPartnerCollage) return;
                  setOpeningPartnerCollage(true);
                  try {
                    await onOpenPartnerCollage(otherUid, other.displayName);
                  } finally {
                    setOpeningPartnerCollage(false);
                  }
                }}
                disabled={openingPartnerCollage}
                aria-label={`View collage: ${other.displayName || 'user'}`}
              >
                <span className="messaging-partner-collage-name">{other.displayName}</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="messaging-partner-collage-arrow"
                  aria-hidden
                >
                  <path d="M7 17 17 7M7 7h10v10" />
                </svg>
              </button>
            ) : (
              <h3 className="messaging-title">{other.displayName}</h3>
            )}
          </div>
          <button className="messaging-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="messaging-messages">
          {messages.length === 0 && !bothUnmasked && (
            <p className="messaging-empty">Send a message to start the conversation</p>
          )}
          {(() => {
            let unmaskInsertIndex = -1;
            if (bothUnmasked) {
              const activeUnmasks = new Set();
              for (let i = 0; i < messages.length; i++) {
                const m = messages[i];
                if (!m.isSystemMessage) continue;
                if (
                  m.text.includes('wants to unmask') ||
                  m.text.includes("You're both unmasked")
                ) {
                  activeUnmasks.add(m.senderId);
                } else if (m.text.includes('cancelled the unmask')) {
                  activeUnmasks.delete(m.senderId);
                }
                if (activeUnmasks.size === 2) { unmaskInsertIndex = i; break; }
              }
            }
            let lastNonSystemMessageIndex = -1;
            for (let j = messages.length - 1; j >= 0; j -= 1) {
              if (!messages[j]?.isSystemMessage) {
                lastNonSystemMessageIndex = j;
                break;
              }
            }
            const showCollageResponseHint =
              hasPrimaryCollage === false &&
              typeof onStartCollage === 'function' &&
              lastNonSystemMessageIndex >= 0 &&
              messages[lastNonSystemMessageIndex]?.senderId === currentUser.uid;
            const elements = [];
            messages.forEach((msg, i) => {
              if (msg.isSystemMessage) {
                if (msg.vvtfMatchIntro || msg.jungianMatchIntro) {
                  elements.push(
                    <div key={msg.id} className="messaging-system-msg messaging-vvtf-intro">
                      <span>{msg.text}</span>
                      {msg.gifUrl ? (
                        <img src={msg.gifUrl} alt="" className="messaging-bubble-gif messaging-vvtf-intro-gif" />
                      ) : null}
                    </div>,
                  );
                  return;
                }
                if (isDailyVibeShareResponseMessage(msg)) {
                  const isMe = msg.senderId === currentUser.uid;
                  elements.push(
                    <div key={msg.id} className="messaging-system-msg">
                      <span>
                        {formatDailyVibeShareResponseDisplay({
                          msg,
                          isSender: isMe,
                          senderName: other.displayName,
                        })}
                      </span>
                    </div>,
                  );
                  return;
                }
                if (isDailyVibeShareInviteMessage(msg)) {
                  const isMe = msg.senderId === currentUser.uid;
                  /* Recipient sees ACCEPT/DECLINE while the share is still pending. */
                  const showActions = !isMe && dailyVibeShareStatus === 'pending';
                  elements.push(
                    <div key={msg.id} className="messaging-system-msg messaging-vibe-share-invite">
                      <span>
                        {formatDailyVibeShareInviteDisplay({
                          isSender: isMe,
                          senderName: other.displayName,
                        })}
                      </span>
                      {showActions ? (
                        <div className="messaging-vibe-share-actions">
                          <button
                            type="button"
                            className="messaging-vibe-share-btn messaging-vibe-share-btn--accept"
                            disabled={vibeShareActionPending}
                            onClick={handleAcceptVibeShare}
                          >
                            {vibeShareActionPending ? '…' : 'ACCEPT'}
                          </button>
                          <button
                            type="button"
                            className="messaging-vibe-share-btn messaging-vibe-share-btn--decline"
                            disabled={vibeShareActionPending}
                            onClick={handleDeclineVibeShare}
                          >
                            DECLINE
                          </button>
                        </div>
                      ) : null}
                      {!isMe && dailyVibeShareStatus === 'accepted' ? (
                        <span className="messaging-vibe-share-hint">You&apos;re now sharing daily vibe checks</span>
                      ) : null}
                    </div>,
                  );
                  return;
                }
                const isMe = msg.senderId === currentUser.uid;
                const senderName = isMe ? 'You' : (other.displayName || 'Someone');
                const isUnmaskRequest = msg.text.includes('wants to unmask');
                const isMutualUnmaskLine = msg.text.includes("You're both unmasked");
                const displayText =
                  isMe && isUnmaskRequest
                    ? 'You proposed to unmask! 👀'
                    : isMe && isMutualUnmaskLine
                      ? "You're both unmasked. Go ahead and say hi! 👀"
                      : `${senderName} ${msg.text}`;
                const showCancel = isMe && isUnmaskRequest && iUnmasked && !bothUnmasked;
                const showWaiting = showCancel;
                elements.push(
                  <div key={msg.id} className="messaging-system-msg">
                    <span>{displayText}</span>
                    {showWaiting && (
                      <span className="messaging-system-msg-hint">Waiting for {other.displayName || 'them'} to respond</span>
                    )}
                    {showCancel && (
                      <button
                        type="button"
                        className="messaging-unmask-cancel"
                        onClick={async () => {
                          try {
                            await cancelUnmask(selectedConversationId, myUid);
                          } catch (err) {
                            console.error('Cancel unmask failed:', err);
                          }
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                );
              } else {
                const isSent = msg.senderId === currentUser.uid;
                const isVibeCheckGif = isDailyVibeCheckGifMessage(msg);
                const bubbleClass = `messaging-bubble ${isSent ? 'sent' : 'received'}${isVibeCheckGif ? ' messaging-bubble--vibe-check-gif' : ''}`;
                const bubbleBody = (
                  <>
                    {isVibeCheckGif ? (
                      <span className="messaging-vibe-check-gif-label">{DAILY_VIBE_CHECK_GIF_TEXT}</span>
                    ) : null}
                    {msg.gifUrl ? (
                      <img src={msg.gifUrl} alt={msg.text || 'GIF'} className="messaging-bubble-gif" />
                    ) : (
                      <p className="messaging-bubble-text">{msg.text}</p>
                    )}
                    <span className="messaging-bubble-meta">
                      {formatTime(msg.timestamp)}
                      {msg.source === 'email' && ' · via email'}
                    </span>
                  </>
                );
                const showHintUnderThisBubble =
                  showCollageResponseHint && isSent && i === lastNonSystemMessageIndex;
                if (showHintUnderThisBubble) {
                  elements.push(
                    <div key={`${msg.id}-no-response-hint`} className="messaging-no-response-hint-wrap">
                      <div className="messaging-bubble sent">{bubbleBody}</div>
                      <p className="messaging-no-response-hint">
                        You&apos;re more likely to get a response when you have your collage as an intro.
                      </p>
                      <button
                        type="button"
                        className="poster-collage-btn messaging-empty-poster-collage-btn messaging-thread-collage-btn messaging-make-collage-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStartCollage(e);
                        }}
                      >
                        Create your profile
                      </button>
                    </div>,
                  );
                } else {
                  elements.push(
                    <div key={msg.id} className={bubbleClass}>
                      {bubbleBody}
                    </div>,
                  );
                }
              }
              if (i === unmaskInsertIndex) {
                elements.push(
                  <div key="unmask-reveal" className="messaging-system-msg">
                    <span>You're both unmasked! 🎉</span>
                  </div>,
                  <div key="unmask-my-photo" className="messaging-bubble sent">
                    <img src={unmaskData[myUid].photoUrl} alt="You" className="messaging-bubble-gif" />
                    <span className="messaging-bubble-meta">You</span>
                  </div>,
                  <div key="unmask-their-photo" className="messaging-bubble received">
                    <img src={unmaskData[otherUid].photoUrl} alt={other.displayName} className="messaging-bubble-gif" />
                    <span className="messaging-bubble-meta">{other.displayName}</span>
                  </div>
                );
              }
            });
            return elements;
          })()}
          <div ref={messagesEndRef} />
        </div>
        {bothUnmasked ? (
          <form className="messaging-input-bar" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              className="messaging-input"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              disabled={sending}
            />
            <button
              type="submit"
              className="messaging-send"
              disabled={!newMessage.trim() || sending}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </form>
        ) : showChatGifPicker ? (
          <div className="messaging-gif-picker-panel">
            <div className="messaging-gif-grid-scroll" ref={chatGifGridScrollRef}>
              <div className="heart-gif-grid">
                {(loadingChatGifs && chatGifs.length === 0
                  ? Array.from({ length: 9 }, (_, i) => ({ id: `ph-${i}`, placeholder: true }))
                  : chatGifs
                ).map((gif) => {
                  if (gif.placeholder) {
                    return <div key={gif.id} className="heart-gif-tile heart-gif-placeholder" />;
                  }
                  return (
                    <button
                      key={gif.id}
                      className="heart-gif-tile"
                      disabled={sending}
                      onClick={async () => {
                        setSending(true);
                        try {
                          await sendMessage(selectedConversationId, currentUser.uid, '', { gifUrl: gif.fullUrl || gif.gifUrl });
                          setShowChatGifPicker(false);
                          setChatGifSearch('');
                        } catch (err) {
                          console.error('Failed to send GIF:', err);
                        } finally {
                          setSending(false);
                        }
                      }}
                    >
                      <span className="heart-gif-spinner" />
                      <img
                        src={gif.stillUrl || gif.gifUrl}
                        alt={gif.title}
                        className="heart-gif-tile-img"
                        loading="lazy"
                        onLoad={(e) => {
                          e.target.parentElement.querySelector('.heart-gif-spinner')?.classList.add('hidden');
                          if (gif.stillUrl && e.target.src !== gif.gifUrl) {
                            e.target.src = gif.gifUrl;
                          }
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="heart-gif-search-wrap">
              <input
                type="text"
                className="heart-gif-search-input"
                placeholder="Search KLIPY GIFs..."
                value={chatGifSearch}
                onChange={(e) => setChatGifSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && chatGifSearch.trim()) {
                    appendKlipyGifs(chatGifSearch.trim(), true);
                  }
                }}
              />
              <button
                type="button"
                className="heart-gif-search-btn"
                onClick={() => {
                  if (chatGifSearch.trim()) appendKlipyGifs(chatGifSearch.trim(), true);
                }}
                disabled={!chatGifSearch.trim() || loadingChatGifs}
              >
                SEARCH
              </button>
            </div>
            <button
              type="button"
              className="messaging-gif-picker-close"
              onClick={() => { setShowChatGifPicker(false); setChatGifSearch(''); }}
            >
              ‹ Back
            </button>
          </div>
        ) : (
          <div className="messaging-pre-unmask-bar">
            <div className="messaging-pre-unmask-buttons">
              <button
                type="button"
                className="messaging-pre-unmask-btn messaging-pre-unmask-btn-gif"
                onClick={() => {
                  localChatGifsBaseRef.current = getShuffledMoodAndHelloGifsAll();
                  setChatGifs(mergeLocalThenKlipy(localChatGifsBaseRef.current, []));
                  setShowChatGifPicker(true);
                  setChatGifSearch('');
                  void appendKlipyGifs('hello', false);
                }}
              >
                SEND A GIF
              </button>
              {iUnmasked ? (
                <span className="messaging-pre-unmask-btn messaging-pre-unmask-btn-waiting">
                  YOU PROPOSED TO UNMASK
                </span>
              ) : (
                <button
                  type="button"
                  className="messaging-pre-unmask-btn messaging-pre-unmask-btn-unmask"
                  onClick={handleUnmask}
                  disabled={unmasking}
                >
                  {unmasking ? 'Unmasking…' : 'UNMASK'}
                </button>
              )}
            </div>
            <p className="messaging-pre-unmask-hint">
              You can start texting after unmasking. You will get unmasked only after you both confirm.
            </p>
          </div>
        )}
        {unmaskNeedCollageOpen && (
          <div
            className="messaging-delete-overlay"
            onClick={() => setUnmaskNeedCollageOpen(false)}
            role="presentation"
          >
            <div className="messaging-delete-popup" onClick={(e) => e.stopPropagation()}>
              <p className="messaging-delete-text">First create your collage then unmask</p>
              <div className="messaging-unmask-collage-actions">
                {typeof onStartCollage === 'function' ? (
                  <button
                    type="button"
                    className="poster-collage-btn messaging-empty-poster-collage-btn messaging-thread-collage-btn messaging-unmask-collage-primary messaging-make-collage-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnmaskNeedCollageOpen(false);
                      onStartCollage(e);
                    }}
                  >
                    Create your profile
                  </button>
                ) : null}
                <button
                  type="button"
                  className="messaging-delete-btn messaging-delete-btn-cancel messaging-unmask-collage-dismiss"
                  onClick={() => setUnmaskNeedCollageOpen(false)}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;
